/**
 * ProgressTabMobile — vertical mobile layout.
 * Spec §4.2, visual ref 02-mobile.html.
 *
 * Layout:
 *   HeadlineCard (scores)
 *   ScoresExplainer (same toggle as desktop)
 *   PillarAccordion × 3 (SEO / AEO / GEO)
 *   AttributionStack
 *   RollupChart
 *
 * NOT a CSS-stacked desktop layout — separate component tree.
 */

import { useState } from 'react';
import { ScoreTag } from './ScoreTag';
import { ScoreCard } from './ScoreCard';
import { AttributionCard } from './AttributionCard';
import { RollupChart } from './RollupChart';
import { HeadlineBand } from './HeadlineBand';
import { PlatformBreakdown } from './PlatformBreakdown';
import { TruncationBanner } from './TruncationBanner';
import { buildDrilldownGroups } from './buildDrilldownGroups';
import type { DateRange } from '../../hooks/useProgressData';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuditResults = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Attribution = Record<string, any>;

interface Props {
  status: 'loading' | 'success' | 'error';
  error?: { message: string; retry: () => void };
  thenAudit: AuditResults | null;
  nowAudit: AuditResults | null;
  windowAudits: AuditResults[];
  attributions: Attribution[];
  thenCitationRate: number | null;
  nowCitationRate: number | null;
  thenAiMentionShare: number | null;
  nowAiMentionShare: number | null;
  headlineNarrative: string | null;
  issuesFixed: number;
  visitsGained: number;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  domainId: string;
  customerTier: string;
}

function scoreDelta(
  then: number | null,
  now: number | null,
): { direction: 'improved' | 'worsened' | 'unchanged' | 'incomparable'; display: string } {
  if (then === null || now === null) return { direction: 'incomparable', display: '—' };
  const diff = now - then;
  if (diff === 0) return { direction: 'unchanged', display: '+0' };
  return { direction: diff > 0 ? 'improved' : 'worsened', display: `${diff > 0 ? '+' : ''}${diff}` };
}

export function ProgressTabMobile({
  thenAudit,
  nowAudit,
  windowAudits,
  attributions,
  thenCitationRate,
  nowCitationRate,
  thenAiMentionShare,
  nowAiMentionShare,
  headlineNarrative,
  issuesFixed,
  visitsGained,
  dateRange,
  error,
  domainId,
  customerTier,
}: Props) {
  const [expandedScore, setExpandedScore] = useState<'seo' | 'aeo' | 'geo' | null>(null);

  function toggleScore(s: 'seo' | 'aeo' | 'geo') {
    setExpandedScore((prev) => (prev === s ? null : s));
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center">
        <p className="text-rose-700 font-medium text-sm">{error.message}</p>
        <button onClick={error.retry} className="mt-2 text-sm text-rose-600 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-2">
      {/* Date indicator */}
      <div className="text-xs text-slate-400 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {dateRange.from} → {dateRange.to}
      </div>

      {/* Headline band — 5-stat stacked (§A mobile) */}
      <HeadlineBand
        issuesFixed={issuesFixed}
        visitsGained={visitsGained}
        thenScore={thenAudit?.overall?.value ?? null}
        nowScore={nowAudit?.overall?.value ?? null}
        thenCitationRate={thenCitationRate}
        nowCitationRate={nowCitationRate}
        thenAiMentionShare={thenAiMentionShare}
        nowAiMentionShare={nowAiMentionShare}
        narrative={headlineNarrative}
        mobile
      />

      {/* Score cards */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-700">Current scores</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {(['seo', 'aeo', 'geo'] as const).map((s) => (
            <div key={s} className="text-center">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{s.toUpperCase()}</p>
              <ScoreTag score={nowAudit?.scores?.[s]?.value ?? null} kind={s} size="md" />
            </div>
          ))}
          <div className="text-center ml-auto">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">AI Visibility</p>
            <ScoreTag score={nowAudit?.overall?.value ?? null} kind="overall" size="md" />
          </div>
        </div>
      </div>

      {/* Truncation upsell banner (A5.3) */}
      <TruncationBanner
        truncationBlock={nowAudit?.crawlerTruncation ?? null}
        customerTier={customerTier}
        dismissKey={domainId}
      />

      {/* Pillar accordions */}
      {(['seo', 'aeo', 'geo'] as const).map((score) => (
        <ScoreCard
          key={score}
          score={score}
          thenValue={thenAudit?.scores?.[score]?.value ?? null}
          nowValue={nowAudit?.scores?.[score]?.value ?? null}
          delta={scoreDelta(
            thenAudit?.scores?.[score]?.value ?? null,
            nowAudit?.scores?.[score]?.value ?? null,
          )}
          expanded={expandedScore === score}
          onToggle={() => toggleScore(score)}
          drilldownGroups={buildDrilldownGroups(
            score,
            thenAudit,
            nowAudit,
            score === 'geo'
              ? { then: thenAudit?.offDomain, now: nowAudit?.offDomain }
              : undefined,
          )}
          confidence={(nowAudit?.scores?.[score]?.confidence ?? 'medium') as 'high' | 'medium' | 'low'}
        />
      ))}

      {/* Per-platform citation rate breakdown (GEO) */}
      {(nowAudit?.geoSweep?.platforms ?? []).length > 0 && (
        <PlatformBreakdown
          platforms={nowAudit?.geoSweep?.platforms ?? []}
          thenPlatforms={thenAudit?.geoSweep?.platforms ?? []}
          customerTier={customerTier}
        />
      )}

      {/* Attribution stack */}
      {attributions.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Fix attributions ({attributions.length})
          </h3>
          {attributions.map((a) => (
            <AttributionCard key={a.id} attribution={a} mobile />
          ))}
        </section>
      )}

      {/* Rollup chart */}
      <section>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Score history</h3>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <RollupChart audits={windowAudits} attributions={attributions} />
        </div>
      </section>
    </div>
  );
}
